import axios from 'api/http-client';
import { appConfig } from 'config/app-config';
import { DefaultError } from 'errors/DefaultError';
import { ApiError } from 'errors/ApiError';
import { SessionError } from 'errors/SessionError';
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  Method
} from 'axios';
import type {
  ErrorResponse,
  NormalizedError,
  NormalizedResponseError,
  RawErrorObject
} from 'errors/types';

const { API_URL } = appConfig;

type TClientParams = AxiosRequestConfig & {
  method?: Method;
  body?: object;
};

export async function client(
  endpoint: string,
  { method, body, ...customConfig }: TClientParams = {},
  apiUrl: string = API_URL
) {
  const headers = {
    ...customConfig.headers,
    'Content-Type': 'application/json; charset=UTF-8'
  };
  const requestConfig = {
    method,
    withCredentials: true,
    ...customConfig,
    headers
  };

  if (body) {
    requestConfig.data = JSON.stringify(body);
  }

  const url = `${apiUrl}/${endpoint}`;
  const response = await axios(url, requestConfig);

  return response.data;
}

export type TError = {
  response?: ErrorResponse;
  message?: string;
};

// eslint-disable-next-line complexity
export const getErrors = (error?: TError): NormalizedError[] | null => {
  if (axios.isCancel(error)) {
    return null;
  }
  if (error instanceof DefaultError) {
    return [(error as DefaultError).toJson()];
  }
  const responseErrors = error?.response?.data?.errors;
  const hasApiValidationErrors = !!responseErrors?.length;
  const isSessionError =
    responseErrors?.length === 1 &&
    responseErrors?.[0].code === 'ForbiddenException';

  if (isSessionError) {
    const sessionError = new SessionError({ error: error?.response });
    return [sessionError.toJson()];
  }

  if (hasApiValidationErrors) {
    const apiResponseError = new ApiError({
      error: error?.response as ErrorResponse
    });
    return (apiResponseError.response as { errors: NormalizedResponseError[] })
      ?.errors;
  }

  try {
    const apiErrorMessage = `${error?.message ?? ''} ${
      error?.response?.config?.url ?? ''
    }`.trim();
    const apiError = new ApiError({
      error: error?.response as ErrorResponse,
      message: apiErrorMessage
    });

    return [apiError.toJson()];
  } catch (error) {
    const caughtError = error as Error;

    return [
      new DefaultError({
        error: caughtError,
        message: caughtError?.message
      }).toJson()
    ];
  }
};

const onResponse = (response: AxiosResponse) => {
  return normalizeResponse(response);
};

const onResponseError = (error: AxiosError) => {
  return Promise.reject(normalizeResponseError(error));
};

axios.interceptors.response.use(onResponse, onResponseError);

type ResponseDetails = {
  isDataHTML: boolean;
  isErrors: boolean;
  isErrorObject: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  isDataObject: boolean;
};

export function getResponseDetails(response: AxiosResponse): ResponseDetails {
  const contentType = response?.headers?.['content-type'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = response?.data as any;
  const isDataObject = data && typeof data === 'object';
  // for aem html error response
  const isDataHTML =
    data &&
    typeof data === 'string' &&
    (contentType?.includes('text/html') ||
      data.startsWith('<!DOCTYPE') ||
      data.startsWith('<html'));
  // for normal error response with errors array
  const isErrors =
    isDataObject && 'errors' in data && Array.isArray(data.errors);
  // for aad error object
  const isErrorObject =
    isDataObject && 'error' in data && typeof data.error === 'object';
  return { isDataHTML, isErrors, isErrorObject, data, isDataObject };
}

export function normalizeResponse(response: AxiosResponse): AxiosResponse {
  const { isErrors, data } = getResponseDetails(response);
  if (isErrors) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorsArray = data.errors as any[];
    const normalizedErrors: NormalizedResponseError[] = errorsArray.map((err) =>
      normalizeErrorObject(err, response)
    );
    response.data.errors = normalizedErrors;
  }
  return response;
}

export function normalizeResponseError(error: AxiosError): AxiosError {
  const { isDataHTML, isErrors, isErrorObject, data } = getResponseDetails(
    error.response as AxiosResponse
  );

  if (isDataHTML) {
    error.response!.data = {
      errors: [normalizeErrorObject({}, error.response, error)]
    };
  }

  if (isErrors) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorsArray = data.errors as any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error.response!.data as any).errors = errorsArray.map((err) =>
      normalizeErrorObject(err, error.response, error)
    );
  }

  if (isErrorObject) {
    const errorObj = data.error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error.response!.data! as any).errors = [
      normalizeErrorObject(errorObj, error.response, error)
    ];
  }
  return error;
}

// eslint-disable-next-line complexity
export function normalizeErrorObject(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  response?: AxiosResponse,
  axiosError?: AxiosError
): NormalizedResponseError {
  const rawError = error as RawErrorObject;
  return {
    code: rawError.code || rawError.errorCode?.toString() || '',
    detail: rawError.detail || '',
    errorMsg: rawError.errorMsg || '',
    instance: rawError.instance || response?.config?.url || '',
    keyword: rawError.keyword || '',
    message:
      rawError.errorMessage ||
      error.message ||
      axiosError?.message ||
      'Unexpected Error',
    status:
      rawError.status?.toString() ||
      error.status?.toString() ||
      error.statusCode?.toString() ||
      response?.status?.toString() ||
      '',

    time: error.time || new Date().toISOString()
  };
}
